import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEurUsdRate } from '../services/priceService';
import { useAutoRefresh } from './useAutoRefresh';
import { PROXY_BASE } from '../config/apiConfig';
import { getAuthHeaders } from '../services/authService';
const CACHE_KEY = 'bullivo_discover_ranked_v1';
const REBOUND_KEY = 'bullivo_rebound_mode';

function loadReboundMode() {
  try { return localStorage.getItem(REBOUND_KEY) === '1'; } catch { return false; }
}

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveCache(assets) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(assets)); } catch {}
}

// Xetra öffnet 9:00 CET, US-Börse schließt 22:00 CET
// → während Handelszeit (Mo-Fr 9-22 Uhr CET) alle 5 min, sonst 30 min
function getMarketRefreshInterval() {
  const now = new Date();
  const day = now.getDay(); // 0=So, 6=Sa
  if (day === 0 || day === 6) return 30 * 60_000;
  const berlinTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  const cetMin  = berlinTime.getHours() * 60 + berlinTime.getMinutes();
  const isOpen  = cetMin >= 9 * 60 && cetMin < 22 * 60;
  return isOpen ? 5 * 60_000 : 30 * 60_000;
}

function nextActionFromSignal(signal, risk, fallingKnife) {
  if (fallingKnife)           return 'Fundamentale Lage prüfen – Nachrichten lesen';
  if (signal === 'Kaufen')    return risk === 'Hoch' ? 'Nur mit engem Stop-Loss einsteigen' : 'Gestaffelten Einstieg prüfen';
  if (signal === 'Verkaufen') return risk === 'Hoch' ? 'Absichern oder Position reduzieren' : 'Beobachten – Abwärtstrend möglich';
  return 'Position halten – keine klare Richtung';
}

export function useDiscoverFeed() {
  const hasData                       = useRef(!!loadCache());
  const [assets, setAssets]           = useState(() => loadCache() || []);
  const [loading, setLoading]         = useState(() => !loadCache());
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    // Show spinner only on first load when no cached data exists
    if (!hasData.current) setLoading(true);
    try {
      const [rankedRes, eurUsd] = await Promise.all([
        getAuthHeaders().then(h => fetch(`${PROXY_BASE}/api/discover/ranked${loadReboundMode() ? '?mode=rebound' : ''}`, { headers: h })).then(r => {
          if (!r.ok) throw new Error(`ranked ${r.status}`);
          return r.json();
        }),
        fetchEurUsdRate(),
      ]);

      // Map server-ranked signals to the asset format the UI expects
      const valid = rankedRes
        .filter(s => s.price > 0)
        .map(s => {
          const priceEur = s.price / eurUsd; // Server returns USD
          return {
            ticker:                  s.ticker,
            name:                    s.name,
            price:                   parseFloat(priceEur.toFixed(priceEur < 1 ? 4 : 2)),
            change:                  s.change24h,
            change7d:                s.change7d,
            currency:                'EUR',
            signal:                  s.signal,
            confidence:              s.confidence,
            risk:                    s.risk || 'Mittel',
            reasons:                 s.reasons || [],
            rsi:                     s.rsi ?? null,
            week52Position:          s.week52Position ?? null,
            fallingKnife:            false,
            recommendationMean:      s.recommendationMean ?? null,
            numberOfAnalystOpinions: s.numberOfAnalystOpinions ?? null,
            targetMeanPrice:         s.targetMeanPrice ?? null,
            recommendationKey:       null,
            nextAction:              nextActionFromSignal(s.signal, s.risk, false),
            type:                    'stock',
            liveData:                true,
            sector:                  s.sector ?? null,
            industry:                s.industry ?? null,
            trailingPE:              s.trailingPE ?? null,
            forwardPE:               s.forwardPE ?? null,
            priceToCashflow:         s.priceToCashflow ?? null,
            marketCap:               s.marketCap ?? null,
            week52Low:               s.week52Low ?? null,
            week52High:              s.week52High ?? null,
            // Quality-Rerank-Felder (09.06.2026): Server liefert sie seit Mai,
            // wurden hier nie gemappt -> DiscoverView-Sortierung lief ins Leere
            qualityScore:            s.qualityScore ?? null,
            qualityReasons:          s.qualityReasons ?? null,
          };
        });

      setAssets(valid);
      saveCache(valid);
      hasData.current = true;
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('[useDiscoverFeed]', err);
      // Nur Error setzen wenn keine gecachten Daten vorhanden
      if (!hasData.current) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Recalculated every render; useAutoRefresh only restarts when the value changes
  useAutoRefresh(load, getMarketRefreshInterval());

  // Rebound-Toggle → Cache leeren + sofort neu laden
  useEffect(() => {
    const handler = () => { hasData.current = false; load(); };
    window.addEventListener('bullivo:rebound-toggle', handler);
    return () => window.removeEventListener('bullivo:rebound-toggle', handler);
  }, [load]);

  return { assets, loading, error, lastUpdated, refresh: load };
}
